/**
 * Fatura kalemlerindeki sayısal giriş alanlarının metin (display) yönetimi.
 *
 * Neden gerekli: sayısal alanlar hesaplama için her zaman dolu olmalı, ama kullanıcı
 * alanı tamamen silebilmeli. Bu yüzden her sayısal alanın bir de metin eşi var
 * (displayQuantity deseninin fiyat/KDV/indirim alanlarına yayılmış hâli). Metin boşsa
 * alan boş görünür, sayısal karşılığı sıfır olur ve kaydetme sırasında uyarılır.
 */

import { InvoiceItem } from '../components/types/invoice.types';
import { recalculateLineItem } from './invoiceCalculations';
import { formatAmount, parseAmountInput, parseTrAmount } from '../../../../utils/numberFormat';

/** Kullanıcının serbestçe boşaltabildiği sayısal alanlar. */
export type EditableNumberField = 'price' | 'vatRate' | 'discountRate' | 'discountAmount';

/** Kaydetme öncesi dolu olması beklenen alanlar. İndirim boş kalabilir (sıfır sayılır). */
export type RequiredNumberField = 'quantity' | 'price' | 'vatRate';

type DisplayKey = 'displayPrice' | 'displayVatRate' | 'displayDiscountRate' | 'displayDiscountAmount';

const DISPLAY_KEY: Record<EditableNumberField, DisplayKey> = {
  price: 'displayPrice',
  vatRate: 'displayVatRate',
  discountRate: 'displayDiscountRate',
  discountAmount: 'displayDiscountAmount',
};

/** Binlik ayraçlı iki ondalıkla gösterilen alanlar; oranlar sade sayı olarak yazılır. */
const MONEY_FIELDS: EditableNumberField[] = ['price', 'discountAmount'];

const currentNumber = (item: InvoiceItem, field: EditableNumberField): number => {
  switch (field) {
    case 'price':
      return item.enteredPrice ?? 0;
    case 'vatRate':
      return item.vatRate ?? 0;
    case 'discountRate':
      return item.discountRate ?? 0;
    case 'discountAmount':
      return item.discountAmount ?? 0;
  }
};

/**
 * Alanın ekranda görünecek metni. Metin hiç girilmemişse (undefined) sayısal
 * değerden üretilir; boş metin ise olduğu gibi korunur.
 */
export const displayTextFor = (item: InvoiceItem, field: EditableNumberField): string => {
  const stored = item[DISPLAY_KEY[field]];
  if (stored !== undefined) return stored;
  const value = currentNumber(item, field);
  return MONEY_FIELDS.includes(field) ? formatAmount(value) : String(value);
};

/** Kullanıcı yazarken yalnızca metni günceller; hesaplama yapılmaz. */
export const setItemDisplay = (item: InvoiceItem, field: EditableNumberField, raw: string): InvoiceItem => ({
  ...item,
  [DISPLAY_KEY[field]]: raw,
});

/**
 * Oranlar 0-100 aralığına sıkıştırılır (backend [Range(0,100)] doğrulamasına takılmamak
 * için), tutarlar negatif olamaz.
 */
const clampForField = (field: EditableNumberField, value: number): number => {
  if (field === 'vatRate' || field === 'discountRate') return Math.max(0, Math.min(100, value));
  return Math.max(0, value);
};

/**
 * Odak çıkışında / Enter'da metni sayıya çevirip kalemi yeniden hesaplar.
 * - Metin boşsa: sayısal değer 0 olur, metin boş kalır (otomatik "0" yazılmaz).
 * - Metin geçersizse: önceki sayısal değere dönülür.
 */
export const commitItemNumber = (item: InvoiceItem, field: EditableNumberField): InvoiceItem => {
  const raw = displayTextFor(item, field).trim();
  const isEmpty = raw === '';
  // Tutarlarda Türkçe yazım: "1.500" = 1500 (parseAmountInput tek noktayı ondalık sayıp 1,5 okuyordu).
  const parsed = MONEY_FIELDS.includes(field) ? parseTrAmount(raw) : parseAmountInput(raw);
  const fallback = currentNumber(item, field);

  const resolved = isEmpty ? 0 : isNaN(parsed) || parsed < 0 ? fallback : parsed;
  const value = clampForField(field, resolved);
  const text = isEmpty ? '' : MONEY_FIELDS.includes(field) ? formatAmount(value) : String(value);

  switch (field) {
    // Fiyat ve KDV satır tutarını değiştirir; indirim tutarı satır tutarına göre
    // kısıtlandığı (Math.min) ve oran ondan türetildiği için indirim metinleri
    // sıfırlanır, böylece modeldeki gerçek değerden yeniden üretilirler.
    case 'price':
      return recalculateLineItem(
        clearDiscountDisplays({ ...item, enteredPrice: value, displayPrice: text })
      );
    case 'vatRate':
      return recalculateLineItem(
        clearDiscountDisplays({ ...item, vatRate: value, displayVatRate: text })
      );
    case 'discountRate':
      // PERCENT modunda indirim tutarı türetilir; tutarın eski metni geçersiz kalır.
      return recalculateLineItem({
        ...item,
        discountType: 'PERCENT',
        discountRate: value,
        displayDiscountRate: text,
        displayDiscountAmount: undefined,
      });
    case 'discountAmount':
      // AMOUNT modunda oran türetilir; oranın eski metni geçersiz kalır.
      return recalculateLineItem({
        ...item,
        discountType: 'AMOUNT',
        discountAmount: value,
        displayDiscountAmount: text,
        displayDiscountRate: undefined,
      });
  }
};

/**
 * İndirim modu değiştiğinde iki indirim alanının metinleri geçersizleşir
 * (biri diğerinden türetildiği için). Metinleri sıfırlayıp sayısal değerden
 * yeniden üretilmelerini sağlar.
 */
export const clearDiscountDisplays = (item: InvoiceItem): InvoiceItem => ({
  ...item,
  displayDiscountRate: undefined,
  displayDiscountAmount: undefined,
});

/** Bir alanın kullanıcı tarafından boşaltılmış olup olmadığı. */
export const isFieldEmpty = (item: InvoiceItem, field: RequiredNumberField): boolean => {
  if (field === 'quantity') return (item.displayQuantity ?? String(item.quantity ?? 0)).trim() === '';
  return displayTextFor(item, field === 'price' ? 'price' : 'vatRate').trim() === '';
};

export interface EmptyFieldReport {
  /** Kalemin listedeki sıra numarası (1'den başlar). */
  index: number;
  itemName: string;
  fields: RequiredNumberField[];
}

/**
 * Kaydetme öncesi boş bırakılmış zorunlu sayısal alanları listeler.
 * Boş liste dönerse kayıt engellenmez.
 */
export const findEmptyRequiredFields = (items: InvoiceItem[]): EmptyFieldReport[] => {
  const reports: EmptyFieldReport[] = [];
  items.forEach((item, i) => {
    const fields = (['quantity', 'price', 'vatRate'] as RequiredNumberField[]).filter((f) =>
      isFieldEmpty(item, f)
    );
    if (fields.length > 0) {
      reports.push({ index: i + 1, itemName: item.itemName, fields });
    }
  });
  return reports;
};
